import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Building2, MapPin, Search, Loader2, Globe2, Filter, ChevronRight } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { crmListClients } from "@/lib/crm.functions";
import { ClientLocations } from "@/components/crm/ClientLocations";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/admin/locations")({
  component: AdminLocationsPage,
  head: () => ({
    meta: [
      { title: "Locations Manager — Admin" },
      { name: "description", content: "Admin-only manager to view, add, and remove client locations." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

function AdminLocationsPage() {
  const { isAdmin, loading } = useAuth();
  const listClients = useServerFn(crmListClients);
  const [search, setSearch] = useState("");
  const [cityFilter, setCityFilter] = useState<string>("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-locations-clients"],
    queryFn: () => listClients({ data: {} as any }),
    enabled: !!isAdmin,
  });

  const { data: cities = [] } = useQuery({
    queryKey: ["admin-locations-cities"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cities")
        .select("id, name")
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []) as { id: string; name: string }[];
    },
    enabled: !!isAdmin,
  });

  const cityNameById = useMemo(() => {
    const m = new Map<string, string>();
    cities.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [cities]);

  const clients = useMemo(() => {
    const rows = (data as any)?.clients ?? [];
    const term = search.trim().toLowerCase();
    return rows.filter((c: any) => {
      if (cityFilter !== "all" && c.city_id !== cityFilter) return false;
      if (!term) return true;
      const hay = [c.company_name, c.name, c.subcategory, c.email, c.phone, cityNameById.get(c.city_id)]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(term);
    });
  }, [data, search, cityFilter, cityNameById]);

  const selected = useMemo(
    () => (clients as any[]).find((c) => c.id === selectedId) ?? null,
    [clients, selectedId],
  );

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="max-w-xl mx-auto p-8 text-center">
        <h1 className="text-xl font-semibold">Admins Only</h1>
        <p className="text-sm text-muted-foreground mt-2">
          You need an admin role to manage client locations.
        </p>
        <Link to="/" className="text-primary underline text-sm mt-4 inline-block">
          Back to Home
        </Link>
      </div>
    );
  }

  const totalClients = ((data as any)?.clients ?? []).length;
  const citiesCovered = new Set(
    ((data as any)?.clients ?? []).map((c: any) => c.city_id).filter(Boolean),
  ).size;

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5">
          <Link
            to="/admin"
            className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Admin
          </Link>
          <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="font-heading text-2xl sm:text-3xl font-semibold flex items-center gap-2.5">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary shrink-0">
                  <MapPin className="h-5 w-5" />
                </span>
                <span className="truncate">Locations manager</span>
              </h1>
              <p className="mt-1.5 text-sm text-muted-foreground max-w-xl">
                Add or remove client locations without creating a new CRM contract.
              </p>
            </div>
            <div className="flex gap-2.5">
              <StatTile label="Clients" value={totalClients} icon={Building2} />
              <StatTile label="Cities" value={citiesCovered} icon={Globe2} />
              <StatTile label="Shown" value={clients.length} icon={Filter} />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid gap-5 lg:grid-cols-[340px,1fr] items-start">
        {/* Client list */}
        <aside className="biz-card overflow-hidden flex flex-col lg:sticky lg:top-4 max-h-[80vh]">
          <div className="p-3 border-b bg-muted/40 space-y-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search clients…"
                className="w-full pl-8 pr-3 h-10 rounded-lg border bg-background text-sm outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <select
              value={cityFilter}
              onChange={(e) => {
                setCityFilter(e.target.value);
                setSelectedId(null);
              }}
              className="w-full px-2 h-10 rounded-lg border bg-background text-sm outline-none focus:ring-2 focus:ring-primary/30"
              aria-label="Filter by city"
            >
              <option value="all">All cities</option>
              {cities.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            {cityFilter !== "all" && (
              <button
                type="button"
                onClick={() => setCityFilter("all")}
                className="text-xs font-medium text-primary hover:underline"
              >
                Clear city filter
              </button>
            )}
          </div>
          <div className="flex-1 overflow-auto">
            {isLoading ? (
              <div className="p-6 flex items-center justify-center">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : clients.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                No clients found.
              </div>
            ) : (
              clients.map((c: any) => {
                const active = c.id === selectedId;
                const label = c.company_name || c.name || "Unnamed";
                const cityName = cityNameById.get(c.city_id);
                return (
                  <button
                    key={c.id}
                    onClick={() => setSelectedId(c.id)}
                    className={`biz-row w-full text-left px-3 py-3 transition relative ${
                      active ? "bg-primary/5" : "hover:bg-muted/60"
                    }`}
                  >
                    {active && (
                      <span className="absolute left-0 top-0 bottom-0 w-[3px] rounded-r bg-primary" />
                    )}
                    <div className="flex items-center gap-2.5">
                      <span
                        className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-semibold ${
                          active
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {label.slice(0, 2).toUpperCase()}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{label}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {[cityName, c.subcategory].filter(Boolean).join(" · ") || "No city set"}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/60" />
                    </div>
                  </button>
                );
              })
            )}
          </div>
          <div className="p-2.5 border-t bg-muted/40 text-xs text-muted-foreground text-center">
            {clients.length} client{clients.length === 1 ? "" : "s"}
          </div>
        </aside>

        {/* Locations panel */}
        <section className="biz-card p-4 sm:p-5 min-h-[420px]">
          {!selected ? (
            <div className="h-full min-h-[340px] flex flex-col items-center justify-center text-center">
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-muted text-muted-foreground mb-3">
                <MapPin className="h-6 w-6" />
              </span>
              <p className="text-sm font-medium">Select a client</p>
              <p className="text-xs text-muted-foreground mt-1 max-w-xs">
                Pick a client from the list to view, add or remove their business locations.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-4 pb-4 border-b flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <h2 className="font-heading text-lg font-semibold truncate">
                    {selected.company_name || selected.name}
                  </h2>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                    {cityNameById.get(selected.city_id) && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5">
                        <MapPin className="h-3 w-3" /> {cityNameById.get(selected.city_id)}
                      </span>
                    )}
                    {selected.subcategory && (
                      <span className="rounded-full bg-muted px-2 py-0.5">{selected.subcategory}</span>
                    )}
                    <code className="font-mono opacity-70">{selected.id.slice(0, 8)}</code>
                  </div>
                </div>
                <Link
                  to="/crm/$clientId"
                  params={{ clientId: selected.id }}
                  className="inline-flex items-center gap-1 rounded-lg border px-3 h-9 text-xs font-medium hover:bg-muted"
                >
                  Open in CRM <ChevronRight className="h-3.5 w-3.5" />
                </Link>
              </div>
              <ClientLocations clientId={selected.id} canEdit={true} />
            </>
          )}
        </section>
      </div>
    </div>
  );
}

function StatTile({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: number;
  icon: typeof Building2;
}) {
  return (
    <div className="biz-card px-3 py-2 min-w-[86px]">
      <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="mt-0.5 text-xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}
